import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Droplets, Mountain, Leaf } from 'lucide-react';

export default function Home() {
  useEffect(() => {
    document.title = 'Home - Salt & Stone';
  }, []);

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-start lg:items-center justify-center overflow-hidden bg-black">
        {/* Background - Salt & Stone Bottle */}
        <div className="absolute inset-0">
          {/* Mobile/Tablet bottle - Top Down style: bottle extends past top screen edge */}
          <div className="lg:hidden absolute left-1/2 -translate-x-1/2 top-0 -translate-y-[40%] h-[95vh] w-[90vw]">
            <img
              src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/9uzc6zo8_IMG_7292.jpg"
              alt="Salt & Stone Gin Bottle"
              className="h-full w-full object-contain opacity-50"
            />
            {/* Edge fade gradients to blend into black background - adjusted to show more of the neck */}
            <div className="absolute inset-0 pointer-events-none" style={{
              background: 'radial-gradient(ellipse 80% 70% at center 40%, transparent 45%, #000000 85%)'
            }}></div>
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent via-transparent to-black"></div>
          </div>
          {/* Desktop/Laptop bottle - Right side */}
          <img
            src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/9uzc6zo8_IMG_7292.jpg"
            alt="Salt & Stone Gin Bottle"
            className="hidden lg:block absolute right-0 top-1/2 -translate-y-1/2 h-full w-auto max-w-none object-contain opacity-95 translate-x-[10%]"
          />
          {/* Smooth blend gradients - Desktop only */}
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/50 to-transparent"></div>
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-b from-[#050505]/30 via-transparent to-[#050505]/80"></div>
          <div className="hidden lg:block absolute inset-0 bg-gradient-to-t from-[#050505]/70 via-transparent to-[#050505]/30"></div>
          {/* Mobile/Tablet gradients - subtle top darkening */}
          <div className="lg:hidden absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto pt-0 md:pt-0 lg:pt-0 mt-auto lg:mt-0 mb-48 md:mb-52 lg:mb-0">
          <span className="caption block mb-6 animate-fade-in opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            Cyprus Craft Gin
          </span>
          
          <h1 className="font-['Playfair_Display'] text-5xl md:text-7xl lg:text-8xl text-[#F2F0E9] mb-8 tracking-tight animate-fade-in opacity-0" style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}>
            Salt & Stone
          </h1>
          
          <div className="w-20 h-px bg-[#D4AF37] mx-auto mb-8 animate-fade-in opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}></div>
          
          <p className="font-['Cormorant_Garamond'] text-xl md:text-2xl text-[#F2F0E9]/80 mb-12 leading-relaxed animate-fade-in opacity-0" style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}>
            Where the island meets spirit
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in opacity-0" style={{ animationDelay: '1s', animationFillMode: 'forwards' }}>
            <Link to="/discover" className="btn-primary inline-flex items-center gap-2" data-testid="hero-shop-btn">
              Discover
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/story" className="btn-secondary" data-testid="hero-story-btn">
              Our Story
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-px h-12 bg-gradient-to-b from-[#D4AF37] to-transparent"></div>
        </div>
      </section>

      {/* Brand Story Section */}
      <section className="py-24 md:py-32 px-6">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <div className="story-image aspect-[4/5] overflow-hidden relative">
              <img
                src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/sxb5oisn_Smigies-Trail-8326cfe6.webp"
                alt="Cyprus - Where Sea Meets Mountain"
                className="w-full h-full object-cover brightness-75 saturate-[0.7]"
              />
              <div className="absolute inset-0 bg-[#050505]/30 mix-blend-multiply"></div>
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 via-transparent to-[#050505]/30"></div>
            </div>

            {/* Content */}
            <div>
              <span className="caption block mb-4">The Origin</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl text-[#F2F0E9] mb-8">
                Born Where Sea<br />Meets Mountain
              </h2>
              <div className="w-12 h-px bg-[#D4AF37] mb-8"></div>
              <p className="text-[#F2F0E9]/70 mb-6 leading-relaxed">
                Salt & Stone Gin captures the rare contrasts of Cyprus — a small island where snow and sun, calm and wild, exist side by side.
              </p>
              <p className="text-[#F2F0E9]/70 mb-8 leading-relaxed">
                Thirty botanicals from coasts and peaks unite in harmony, crafting a flavour both vivid and refined.
              </p>
              <Link to="/story" className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-widest hover:gap-4 transition-all">
                Read Our Story
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 md:py-32 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-16">
            <span className="caption block mb-4">The Craft</span>
            <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl text-[#F2F0E9]">
              Distilled Excellence
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Feature 1 */}
            <div className="text-center group">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-500">
                <Leaf className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-3">30 Botanicals</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                Hand-selected from Cyprus coastal regions and mountain peaks
              </p>
            </div>

            {/* Feature 2 */}
            <div className="text-center group">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-500">
                <Droplets className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-3">Unfiltered</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                Rich in natural botanical oils for intense aromatic depth
              </p>
            </div>

            {/* Feature 3 */}
            <div className="text-center group">
              <div className="w-16 h-16 mx-auto mb-6 border border-[#D4AF37]/30 rounded-full flex items-center justify-center group-hover:border-[#D4AF37] transition-colors duration-500">
                <Mountain className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <h3 className="font-['Playfair_Display'] text-xl text-[#F2F0E9] mb-3">Cyprus Origin</h3>
              <p className="text-[#F2F0E9]/60 text-sm leading-relaxed">
                Crafted on an island where Mediterranean contrasts inspire
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Preview */}
      <section className="py-24 md:py-32 px-6 bg-[#111111]">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Content */}
            <div className="order-2 lg:order-1">
              <span className="caption block mb-4">The Spirit</span>
              <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl text-[#F2F0E9] mb-4">
                Salt & Stone Gin
              </h2>
              <p className="text-[#D4AF37] text-sm tracking-widest mb-8">700ML · 43% ABV</p>
              <p className="text-[#F2F0E9]/70 mb-8 leading-relaxed">
                Each bottle captures the essence of Cyprus — where ancient traditions meet modern craft distillation. Unfiltered to preserve the rich botanical oils that define our unique character.
              </p>
              {/* Price */}
              <div className="mb-8">
                <span className="font-['Playfair_Display'] text-4xl text-[#D4AF37]">€47</span>
              </div>
              <Link to="/shop" className="btn-primary inline-flex items-center gap-2" data-testid="product-preview-shop-btn">
                Shop Now
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            {/* Image */}
            <div className="order-1 lg:order-2 relative">
              <div className="relative">
                <img
                  src="https://customer-assets.emergentagent.com/job_60caa5be-1d50-4b72-801b-3659f641dade/artifacts/hyia0i4d_1120597%202.JPEG"
                  alt="Salt & Stone Gin Bottle with Botanicals"
                  className="w-full max-w-md mx-auto brightness-90 saturate-[0.85]"
                />
                {/* Smooth edge blending */}
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'radial-gradient(ellipse 70% 75% at center, transparent 35%, #111111 80%)'
                }}></div>
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'linear-gradient(to right, #111111 0%, transparent 8%, transparent 92%, #111111 100%)'
                }}></div>
                <div className="absolute inset-0 pointer-events-none" style={{
                  background: 'linear-gradient(to bottom, #111111 0%, transparent 5%, transparent 90%, #111111 100%)'
                }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="py-24 md:py-32 px-6 border-t border-white/5">
        <div className="container mx-auto max-w-3xl text-center">
          <div className="w-12 h-px bg-[#D4AF37] mx-auto mb-12"></div>
          <blockquote className="quote mb-8">
            "Born where sea meets mountain, Salt & Stone Gin captures the rare contrasts of Cyprus — a small island where snow and sun, calm and wild, exist side by side."
          </blockquote>
          <div className="w-12 h-px bg-[#D4AF37] mx-auto"></div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 px-6 bg-[#0A0A0A]">
        <div className="container mx-auto max-w-4xl text-center">
          <span className="caption block mb-4">Experience</span>
          <h2 className="font-['Playfair_Display'] text-3xl md:text-4xl lg:text-5xl text-[#F2F0E9] mb-8">
            Discover Salt & Stone
          </h2>
          <p className="text-[#F2F0E9]/70 mb-12 max-w-xl mx-auto">
            Join us in celebrating the spirit of Cyprus. Explore our story, taste our craft, and become part of our journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop" className="btn-primary" data-testid="cta-shop-btn">
              Shop Collection
            </Link>
            <Link to="/contact" className="btn-secondary" data-testid="cta-contact-btn">
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
